import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import {
  ArrowPathIcon,
  BanknotesIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

const today = new Date();
const toInputDate = (date) => date.toISOString().split('T')[0];
const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

const defaultFilters = {
  dateFrom: toInputDate(startOfMonth),
  dateTo: toInputDate(today),
  module: 'all'
};

const typeLabel = (type) => {
  if (type === 'indent' || type === 'intend') return 'Indent';
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Unknown';
};

const moduleLabels = {
  all: 'All Modules',
  expenses: 'Expenses',
  office: 'Office',
  clients: 'Clients',
  cars: 'Cars',
  assets: 'Assets'
};

const formatReportDate = (value) => {
  if (!value) return '';
  return new Date(`${value}T00:00:00`).toLocaleDateString();
};

const defaultSummary = {
  expenses: {
    total: 0,
    pending: { count: 0, amount: 0 },
    approved: { count: 0, amount: 0 },
    rejected: { count: 0, amount: 0 },
    paid: { count: 0, amount: 0 },
    totalAmount: 0,
    byType: {}
  },
  office: {
    total: 0,
    pending: { count: 0, amount: 0 },
    approved: { count: 0, amount: 0 },
    rejected: { count: 0, amount: 0 },
    paid: { count: 0, amount: 0 },
    totalAmount: 0
  },
  clients: { total: 0, totalDues: 0 },
  cars: { total: 0, totalExpenses: 0 },
  assets: { total: 0, currentValue: 0 },
  monthlyOverview: [],
  recentExpenses: [],
  recentOfficeExpenses: []
};

const StatCard = ({ label, value, subtitle, icon: Icon, tone = 'bg-blue-500' }) => (
  <div className="card p-5">
    <div className="flex items-center">
      <div className={`p-3 rounded-lg ${tone}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div className="ml-4 min-w-0">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="text-2xl font-semibold text-gray-900 truncate">{value}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
  </div>
);

const statusRows = (summary) => [
  { status: 'Pending', ...summary.pending, color: '#f59e0b' },
  { status: 'Approved', ...summary.approved, color: '#10b981' },
  { status: 'Paid', ...summary.paid, color: '#3b82f6' },
  { status: 'Rejected', ...summary.rejected, color: '#ef4444' }
];

const Reports = () => {
  const { canAccess } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);
  const [summary, setSummary] = useState(defaultSummary);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!canAccess('reports')) return;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await api.get(`/api/reports/summary?${params.toString()}`);
      setSummary({ ...defaultSummary, ...response.data });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  }, [canAccess, filters]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const setPreset = (preset) => {
    const now = new Date();
    if (preset === 'month') {
      setFilters((prev) => ({ ...prev, dateFrom: toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)), dateTo: toInputDate(now) }));
    }
    if (preset === 'quarter') {
      setFilters((prev) => ({ ...prev, dateFrom: toInputDate(new Date(now.getFullYear(), now.getMonth() - 2, 1)), dateTo: toInputDate(now) }));
    }
    if (preset === 'year') {
      setFilters((prev) => ({ ...prev, dateFrom: toInputDate(new Date(now.getFullYear(), 0, 1)), dateTo: toInputDate(now) }));
    }
    if (preset === 'all') {
      setFilters((prev) => ({ ...prev, dateFrom: '', dateTo: '' }));
    }
  };

  const expenseStatusRows = useMemo(() => statusRows(summary.expenses), [summary.expenses]);
  const expensePieRows = useMemo(
    () => expenseStatusRows.filter((row) => (row.count || 0) > 0),
    [expenseStatusRows]
  );
  const officeStatusRows = useMemo(() => statusRows(summary.office), [summary.office]);
  const expenseTypeRows = useMemo(() => {
    return Object.entries(summary.expenses.byType || {}).map(([type, value]) => ({
      type: typeLabel(type),
      count: value.count || 0,
      amount: value.amount || 0
    }));
  }, [summary.expenses.byType]);

  const pendingValue = (summary.expenses.pending?.amount || 0) + (summary.office.pending?.amount || 0);
  const approvedPayable = (summary.expenses.approved?.amount || 0) + (summary.office.approved?.amount || 0);
  const paidValue = (summary.expenses.paid?.amount || 0) + (summary.office.paid?.amount || 0);
  const reportRangeLabel = filters.dateFrom || filters.dateTo
    ? `${filters.dateFrom ? formatReportDate(filters.dateFrom) : 'Beginning'} to ${filters.dateTo ? formatReportDate(filters.dateTo) : 'Today'}`
    : 'All time';
  const selectedModuleLabel = moduleLabels[filters.module] || 'All Modules';
  const generatedAt = new Date().toLocaleString();
  const moneyCount = (count, amount) => ({
    count: Number(count || 0),
    amount: formatCurrency(amount)
  });
  const moduleSections = [
    {
      key: 'expenses',
      title: 'Expenses',
      rows: [
        ['Total requests', summary.expenses.total || 0],
        ['Pending approval', moneyCount(summary.expenses.pending?.count, summary.expenses.pending?.amount)],
        ['Approved payable', moneyCount(summary.expenses.approved?.count, summary.expenses.approved?.amount)],
        ['Paid out', moneyCount(summary.expenses.paid?.count, summary.expenses.paid?.amount)],
        ['Rejected', moneyCount(summary.expenses.rejected?.count, summary.expenses.rejected?.amount)]
      ]
    },
    {
      key: 'office',
      title: 'Office',
      rows: [
        ['Total bills', summary.office.total || 0],
        ['Pending approval', moneyCount(summary.office.pending?.count, summary.office.pending?.amount)],
        ['Approved payable', moneyCount(summary.office.approved?.count, summary.office.approved?.amount)],
        ['Paid out', moneyCount(summary.office.paid?.count, summary.office.paid?.amount)]
      ]
    },
    {
      key: 'clients',
      title: 'Clients',
      rows: [
        ['Active clients', summary.clients.total || 0],
        ['Current dues', formatCurrency(summary.clients.totalDues)]
      ]
    },
    {
      key: 'cars',
      title: 'Cars',
      rows: [
        ['Vehicles', summary.cars.total || 0],
        ['Vehicle expenses', formatCurrency(summary.cars.totalExpenses)]
      ]
    },
    {
      key: 'assets',
      title: 'Assets',
      rows: [
        ['Assets', summary.assets.total || 0],
        ['Current value', formatCurrency(summary.assets.currentValue)]
      ]
    }
  ];
  const visibleModuleSections = filters.module === 'all'
    ? moduleSections
    : moduleSections.filter((section) => section.key === filters.module);
  const reportRows = useMemo(() => {
    const expenseRows = (summary.recentExpenses || []).map((expense) => ({
      id: `expense-${expense._id}`,
      module: 'Expense',
      type: typeLabel(expense.type),
      purpose: expense.purpose,
      description: expense.description,
      actorLabel: 'Requested by',
      actor: expense.requestedBy?.name || 'N/A',
      employeeId: expense.requestedBy?.employeeId || '',
      status: expense.status,
      amount: expense.amount?.approved || expense.amount?.requested,
      date: expense.date || expense.createdAt
    }));

    const officeRows = (summary.recentOfficeExpenses || []).map((expense) => ({
      id: `office-${expense._id}`,
      module: 'Office',
      type: expense.category,
      purpose: expense.purpose,
      description: expense.description,
      actorLabel: 'Billed by',
      actor: expense.billedBy?.name || 'N/A',
      employeeId: expense.billedBy?.employeeId || '',
      status: expense.status,
      amount: expense.amount,
      date: expense.date || expense.createdAt,
      approvedBy: expense.approvedBy?.name || '',
      paidBy: expense.paidBy?.name || ''
    }));

    return [...expenseRows, ...officeRows]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 15);
  }, [summary.recentExpenses, summary.recentOfficeExpenses]);

  const csvEscape = (value) => {
    const text = String(value ?? '');
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };

  const exportCsv = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.module) params.append('module', filters.module);

      const response = await api.get(`/api/reports/transactions?${params.toString()}`);
      const exportRows = response.data.rows || [];
      const rows = [
        ['Module', 'Record Type', 'Date', 'Description', 'Party', 'Responsible Person', 'Employee ID', 'Status', 'Amount', 'Reference Number', 'Approved By', 'Paid By'],
        ...exportRows.map((row) => [
          row.module,
          typeLabel(row.recordType),
          row.date ? new Date(row.date).toLocaleDateString() : '',
          row.description,
          row.party,
          row.responsibleName,
          row.responsibleEmployeeId,
          row.status,
          Number(row.amount || 0),
          row.referenceNumber,
          row.approvedBy,
          row.paidBy
        ])
      ];
      const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fin-report-${filters.module || 'all'}-${filters.dateFrom || 'all'}-${filters.dateTo || 'all'}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${exportRows.length} report rows`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const printReport = () => {
    window.print();
  };

  if (!canAccess('reports')) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You don't have permission to access reports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600">Financial summary, date filters, and approval/payment visibility</p>
          <p className="mt-1 text-sm text-gray-500">
            Review focus: {selectedModuleLabel} | Range: {reportRangeLabel}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row no-print">
          <button onClick={exportCsv} disabled={exporting} className="btn-secondary flex items-center justify-center disabled:opacity-60">
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <button onClick={printReport} className="btn-secondary flex items-center justify-center">
            Print
          </button>
          <button onClick={fetchSummary} className="btn-secondary flex items-center justify-center">
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      <div className="print-only">
        <h2 className="text-xl font-bold text-gray-900">Fin Accounting Management Report</h2>
        <p className="text-sm text-gray-600">
          Module: {selectedModuleLabel} | Range: {reportRangeLabel} | Generated: {generatedAt}
        </p>
      </div>

      <div className="card p-4 no-print">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-4">
          <select
            value={filters.module}
            onChange={(event) => setFilters((prev) => ({ ...prev, module: event.target.value }))}
            className="input-field"
          >
            <option value="all">All Modules</option>
            <option value="expenses">Expenses</option>
            <option value="office">Office</option>
            <option value="clients">Clients</option>
            <option value="cars">Cars</option>
            <option value="assets">Assets</option>
          </select>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
            className="input-field"
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
            className="input-field"
          />
          <button onClick={() => setPreset('month')} className="btn-secondary">This Month</button>
          <button onClick={() => setPreset('quarter')} className="btn-secondary">3 Months</button>
          <button onClick={() => setPreset('year')} className="btn-secondary">This Year</button>
          <button onClick={() => setPreset('all')} className="btn-secondary">All Time</button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard label="Pending Approval" value={formatCurrency(pendingValue)} subtitle="Expense + office pending value" icon={CreditCardIcon} tone="bg-yellow-500" />
            <StatCard label="Approved Payable" value={formatCurrency(approvedPayable)} subtitle="Ready to be paid" icon={BanknotesIcon} tone="bg-green-500" />
            <StatCard label="Paid Out" value={formatCurrency(paidValue)} subtitle="Marked paid in selected range" icon={BanknotesIcon} tone="bg-blue-500" />
            <StatCard label="Client Dues" value={formatCurrency(summary.clients.totalDues)} subtitle={`${summary.clients.total} active clients`} icon={BuildingOfficeIcon} tone="bg-red-500" />
          </div>

          <div className="card p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Management Review</h2>
              <p className="text-sm text-gray-500">
                Executive snapshot for {selectedModuleLabel.toLowerCase()} in the selected date range. Export CSV for transaction-level detail.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {visibleModuleSections.map((section) => (
                <div key={section.title} className="border border-gray-200 rounded-md p-4 bg-white">
                  <h3 className="font-semibold text-gray-900 mb-3">{section.title}</h3>
                  <div className="space-y-2 text-sm">
                    {section.rows.map(([label, value]) => (
                      <div key={label} className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
                        <span className="text-gray-600 leading-5">{label}</span>
                        {typeof value === 'object' ? (
                          <span className="text-right">
                            <span className="block font-semibold text-gray-900">{value.amount}</span>
                            <span className="block text-xs text-gray-500">{value.count} records</span>
                          </span>
                        ) : (
                          <span className="font-semibold text-gray-900 text-right whitespace-nowrap">{value}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Monthly Spending vs Payments</h2>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={summary.monthlyOverview || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [formatCurrency(value), '']} />
                  <Bar dataKey="expenses" fill="#3b82f6" name="Spending" />
                  <Bar dataKey="income" fill="#10b981" name="Client Payments" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Expense Request Status</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                <div className="md:col-span-2">
                  {expensePieRows.length === 0 ? (
                    <div className="flex items-center justify-center h-[280px] text-sm text-gray-500">
                      No expense requests in this range
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={expensePieRows}
                          dataKey="count"
                          nameKey="status"
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={90}
                          label={({ status, count }) => `${status}: ${count}`}
                        >
                          {expensePieRows.map((row) => (
                            <Cell key={row.status} fill={row.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name, item) => [`${value} records / ${formatCurrency(item.payload.amount)}`, name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <ul className="space-y-2 text-sm">
                  {expenseStatusRows.map((row) => (
                    <li key={row.status} className="flex items-center justify-between">
                      <span className="flex items-center">
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full mr-2"
                          style={{ backgroundColor: row.color }}
                        />
                        <span className="text-gray-700">{row.status}</span>
                      </span>
                      <span className={`font-medium ${(row.count || 0) === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                        {row.count || 0}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Expense Types</h2>
              <div className="space-y-3">
                {expenseTypeRows.length === 0 ? (
                  <p className="text-sm text-gray-500">No expense requests in this range.</p>
                ) : (
                  expenseTypeRows.map((row) => (
                    <div key={row.type} className="flex items-center justify-between border-b border-gray-100 pb-2">
                      <div>
                        <p className="font-medium text-gray-900">{row.type}</p>
                        <p className="text-xs text-gray-500">{row.count} records</p>
                      </div>
                      <p className="font-semibold text-gray-900">{formatCurrency(row.amount)}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Office Status</h2>
              <div className="space-y-3">
                {officeStatusRows.map((row) => (
                  <div key={row.status} className="flex items-center justify-between border-b border-gray-100 pb-2">
                    <div>
                      <p className="font-medium text-gray-900">{row.status}</p>
                      <p className="text-xs text-gray-500">{row.count || 0} records</p>
                    </div>
                    <p className="font-semibold text-gray-900">{formatCurrency(row.amount)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Resource Totals</h2>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-gray-600">Cars</span><span className="font-semibold">{summary.cars.total}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Car Expenses</span><span className="font-semibold">{formatCurrency(summary.cars.totalExpenses)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Assets</span><span className="font-semibold">{summary.assets.total}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Asset Value</span><span className="font-semibold">{formatCurrency(summary.assets.currentValue)}</span></div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center">
              <ChartBarIcon className="h-5 w-5 mr-2 text-gray-500" />
              <h2 className="font-semibold text-gray-900">Recent Accountable Records</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Module</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type / Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purpose</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Responsible</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportRows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">No recent records in this range</td>
                    </tr>
                  ) : (
                    reportRows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.module}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{row.type}</td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-900">{row.purpose}</p>
                          <p className="text-xs text-gray-500 truncate max-w-md">{row.description}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <p>{row.actorLabel}: {row.actor}</p>
                          {row.employeeId && <p className="text-xs text-gray-500">ID: {row.employeeId}</p>}
                          {row.approvedBy && <p className="text-xs text-gray-500">Approved by: {row.approvedBy}</p>}
                          {row.paidBy && <p className="text-xs text-gray-500">Paid by: {row.paidBy}</p>}
                        </td>
                        <td className="px-6 py-4 text-sm capitalize text-gray-600">{row.status}</td>
                        <td className="px-6 py-4 text-right font-semibold text-gray-900">
                          {formatCurrency(row.amount)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
